import React, { useEffect, useState } from 'react';
import {
  Box, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, IconButton, Typography, Chip, TextField, MenuItem,
  InputAdornment, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery, useTheme
} from '@mui/material';
import { Add, Edit, PictureAsPdf, Search, Delete, Visibility, CheckCircle, ContentCopy, PostAdd } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';
import StatusChip from '../common/StatusChip';
import { MobileCard, MobileRow } from '../common/MobileCard';
import { useAuth } from '../../contexts/AuthContext';

export default function InvoiceList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canEdit = isAdmin || isManager;
  const canDelete = isAdmin;
  const [invoices, setInvoices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState(null);
  const navigate = useNavigate();

  const load = async () => { const res = await api.get('/invoices'); setInvoices(res.data.data); };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    let d = invoices;
    if (search)       d = d.filter(i => i.invoice_number.toLowerCase().includes(search.toLowerCase()) || i.beneficiary_name?.toLowerCase().includes(search.toLowerCase()));
    if (statusFilter !== 'all') d = d.filter(i => i.status === statusFilter);
    setFiltered(d);
  }, [search, statusFilter, invoices]);

  const handleDelete = async () => {
    try {
      await api.delete(`/invoices/${deleteDialog.id}`);
      setDeleteDialog(null); load();
    } catch (err) { console.error(err); setDeleteDialog(null); }
  };

  const markAsPaid = async (inv) => {
    try {
      await api.put(`/invoices/${inv.id}/status`, { status: 'paid' });
      load();
    } catch (err) { console.error(err); }
  };

  const openPdf = async (id, number, download = false) => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      if (download) {
        const a = document.createElement('a');
        a.href = url; a.download = `Invoice-${number}.pdf`; a.click();
        URL.revokeObjectURL(url);
      } else {
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      }
    } catch (err) {
      console.error('PDF error', err);
    }
  };

  const totalPending = filtered.filter(i => !['paid', 'cancelled'].includes(i.status))
    .reduce((s, i) => s + parseFloat(i.total_amount || 0), 0);

  return (
    <Box>
      <PageHeader title="Purchase Invoices" subtitle="Invoices for bank remittance"
        action={
          <Button variant="contained" startIcon={<Add />} size={isMobile ? 'small' : 'medium'}
            onClick={() => navigate('/invoices/new')}>
            {isMobile ? 'New' : 'Create Invoice'}
          </Button>
        }
      />

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 160 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        <TextField size="small" select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          sx={{ minWidth: 130 }} label="Status">
          <MenuItem value="all">All Status</MenuItem>
          {['draft','pending','booked','submitted','paid','cancelled'].map(s => (
            <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
          ))}
        </TextField>
      </Box>

      {totalPending > 0 && (
        <Chip label={`Outstanding: $${totalPending.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
          color="error" variant="outlined" sx={{ mb: 1.5 }} />
      )}

      {/* Mobile cards */}
      {isMobile ? (
        <Box>
          {filtered.length === 0
            ? <Card><Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>No invoices found</Box></Card>
            : filtered.map(inv => (
              <MobileCard key={inv.id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {inv.invoice_number}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{inv.invoice_date}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <StatusChip status={inv.status} />
                    <IconButton size="small" onClick={() => navigate(`/invoices/${inv.id}`)} sx={{ color: 'info.main' }}>
                      <Visibility fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => openPdf(inv.id, inv.invoice_number, true)} sx={{ color: 'error.main' }}>
                      <PictureAsPdf fontSize="small" />
                    </IconButton>
                    {canEdit && <IconButton size="small" onClick={() => navigate(`/invoices/${inv.id}/edit`)}><Edit fontSize="small" /></IconButton>}
                    {canEdit && <IconButton size="small" onClick={() => navigate(`/invoices/new?clone=${inv.id}`)} sx={{ color: 'secondary.main' }}><ContentCopy fontSize="small" /></IconButton>}
                    {canEdit && <IconButton size="small" onClick={() => navigate(`/purchase-orders/new?from_pi=${inv.id}`)} sx={{ color: 'warning.main' }}><PostAdd fontSize="small" /></IconButton>}
                    {canEdit && inv.status !== 'paid' && (
                      <Tooltip title="Mark as Paid">
                        <IconButton size="small" onClick={() => markAsPaid(inv)} sx={{ color: 'success.main' }}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && <IconButton size="small" onClick={() => setDeleteDialog(inv)} sx={{ color: 'error.main' }}><Delete fontSize="small" /></IconButton>}
                  </Box>
                </Box>
                <MobileRow label="Supplier"  value={inv.beneficiary_name} />
                <MobileRow label="Amount"    value={`${inv.currency} ${parseFloat(inv.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} bold />
                {inv.inr_amount && <MobileRow label="INR Equiv." value={`₹${parseFloat(inv.inr_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />}
              </MobileCard>
            ))
          }
        </Box>
      ) : (
        /* Desktop table */
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice No.</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Beneficiary</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">INR Equiv.</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Purpose</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No invoices found</TableCell></TableRow>
              ) : filtered.map(inv => (
                <TableRow key={inv.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{inv.invoice_number}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{inv.invoice_date}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{inv.beneficiary_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{inv.country}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>{inv.currency} {parseFloat(inv.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {inv.inr_amount ? `₹ ${parseFloat(inv.inr_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—'}
                  </TableCell>
                  <TableCell><StatusChip status={inv.status} /></TableCell>
                  <TableCell sx={{ fontSize: 11, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {inv.purpose_of_remittance || '—'}
                  </TableCell>
                  <TableCell align="center">
                    {canEdit && <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/invoices/${inv.id}/edit`)}><Edit fontSize="small" /></IconButton></Tooltip>}
                    {canEdit && <Tooltip title="Clone"><IconButton size="small" onClick={() => navigate(`/invoices/new?clone=${inv.id}`)} sx={{ color: 'secondary.main' }}><ContentCopy fontSize="small" /></IconButton></Tooltip>}
                    {canEdit && <Tooltip title="Create Purchase Order from this proforma"><IconButton size="small" onClick={() => navigate(`/purchase-orders/new?from_pi=${inv.id}`)} sx={{ color: 'warning.main' }}><PostAdd fontSize="small" /></IconButton></Tooltip>}
                    {canEdit && inv.status !== 'paid' && (
                      <Tooltip title="Mark as Paid">
                        <IconButton size="small" onClick={() => markAsPaid(inv)} sx={{ color: 'success.main' }}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="View Details"><IconButton size="small" onClick={() => navigate(`/invoices/${inv.id}`)} sx={{ color: 'info.main' }}><Visibility fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Download PDF"><IconButton size="small" onClick={() => openPdf(inv.id, inv.invoice_number, true)} sx={{ color: 'error.main' }}><PictureAsPdf fontSize="small" /></IconButton></Tooltip>
                    {canDelete && <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteDialog(inv)} sx={{ color: 'error.main' }}><Delete fontSize="small" /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Delete Confirmation */}
      <Dialog open={Boolean(deleteDialog)} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete invoice <strong>{deleteDialog?.invoice_number}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
